import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import { Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@material-ui/core';
import { PergoTextField } from 'components/PergoTextField/PergoTextField';
import { CircularProgressContext } from 'components/CircularProgressOverlay';
import { MapContext } from 'context/mapContext/mapContextContainer';
import { CallContext } from 'context/callContextContainer';
import { driversService } from 'services/driversService';

import JobSectionContent from './JobSectionContent';

const Step3 = ({ globalValues, onClickNext, onClickPrev, isCurrentStep, isCallCenter }) => {
  const [isUpdate, setUpdate] = useState(false);
  const [values, setValues] = useState({});
  const [selDriver, setSelDriver] = useState(-1);
  const { mapData } = useContext(MapContext);
  const { siteSetting, callcenterActiveDb } = useContext(CallContext);

  const [formValid, setFormValid] = useState({
    driver_id: 0,
  });

  const [formErrors, setFormErrors] = useState({
    driver_id: '',
  });

  const [drivers, setDrivers] = useState([]);
  const [tagConfirm, setTagConfirm] = useState(false);
  const { showCircularProgress, hideCircularProgress } = useContext(CircularProgressContext);

  // Human list of a candidate's tag conflicts (all of them).
  const tagIssuesText = (tagMatch) => {
    if (!tagMatch || tagMatch.ok !== false) return '';
    return [
      ...(tagMatch.missing_names || []).map(n => `missing ${n}`),
      ...(tagMatch.conflicting_names || []).map(n => `has excluded ${n}`),
    ].join(', ');
  };

  // Commit a driver selection into the wizard values.
  const applyDriver = (newValue) => {
    const isObj = newValue && typeof newValue === 'object';
    setSelDriver(newValue);
    setValues(prev => ({
      ...prev,
      driver_id: isObj ? (newValue.driver_id || newValue.driverId) : newValue,
      vehicle_id: isObj ? (newValue.vehicle_id || newValue.vehicleId) : -1,
    }));
  };

  // On selecting a driver, warn (and require confirmation) if they fail the job's tags.
  const handleSelectDriver = (newValue) => {
    if (newValue && typeof newValue === 'object' && newValue.tag_match && newValue.tag_match.ok === false) {
      setTagConfirm(newValue);
      return;
    }
    applyDriver(newValue);
  };

  useEffect(() => {
    async function initializeStep() {
      setValues({
        ...values,
        driver_id: globalValues.driver_id,
        vehicle_id: globalValues.vehicle_id,
        payments: globalValues.payments,
      });

      showCircularProgress('Loading');
      const data = await driversService.get_drivers_by_method({
        job_info: JSON.stringify({
          start_lat: mapData.pickup_lat * 1000000,
          start_lon: mapData.pickup_lng * 1000000,
          vehicle_type: globalValues.vehicle_type,
          driver_id: 0,
          job_type: 1,
          // Carry the job's tag requirements so the backend can flag candidates
          // that don't satisfy them (warn-but-allow on manual dispatch). The wizard
          // holds these as tags_require/tags_exclude (edited in step 2); fall back to
          // the required_tags/excluded_tags shape for a loaded job.
          required_tags: (globalValues.tags_require || globalValues.required_tags || []).map(t => t.value || t.id),
          excluded_tags: (globalValues.tags_exclude || globalValues.excluded_tags || []).map(t => t.value || t.id),
        }),
        ...(isCallCenter && { dbname: callcenterActiveDb }),
      });

      hideCircularProgress();
      if (data && data.data.status === 'SUCCESS' && data.data.data.num_rows >= 1) {
        setDrivers(data.data.data.data);

        const tmp = data.data.data.data;
        if (tmp?.length > 0 && tmp[0]?.drivers?.length > 0) {
          applyDriver(tmp[0].drivers[0]);
        }
      }
    }

    if (isCurrentStep) {
      initializeStep();
    }
  }, [isCurrentStep]);

  const checkFieldValidate = (id, value) => {
    const fieldValidation = formValid;
    const fieldValidationErrors = formErrors;

    fieldValidation[id] = 1;
    if (id === 'driver_id') {
      fieldValidation[id] = value > 0 ? 1 : -1;
      fieldValidationErrors[id] = fieldValidation[id] === 1 ? '' : 'Please select driver';
    }

    setFormValid(fieldValidation);
    setFormErrors(fieldValidationErrors);
    setUpdate(!isUpdate);

    return fieldValidation[id];
  };

  const checkStepValidate = () => {
    let stepValid = 0;
    let stepFields = [];

    stepFields = ['driver_id'];

    for (var c in stepFields) {
      const element = stepFields[c];
      stepValid += checkFieldValidate(element, values[element]);
    }
    const formValidCount = stepValid === stepFields.length;
    return formValidCount;
  };

  const onNextStep = () => {
    onClickNext(values);
  };

  return (
    <StepContent
      title="Dispatch"
      prevButton={{
        onClick: onClickPrev,
        label: 'Back',
      }}
      middleButton={{
        onClick: onNextStep,
        label: 'Create Dispatch',
      }}
    >
      <PergoTextField label="Distance (Miles)" value={`${globalValues.distance} miles`} readonly />
      <div className="form-control-section mt-4">
        {drivers?.length > 0 ? (
          drivers.map((item, index) => (
            <FormControl variant="outlined" {...(formValid && { error: formValid.driver_id === -1 })} key={index}>
              <InputLabel>{`Select Drivers (${item.driver_method.desc})`}</InputLabel>
              <Select value={selDriver} onChange={e => handleSelectDriver(e.target.value)}>
                {item.drivers &&
                  item.drivers.map((item1, index1) => {
                    const tagBad = item1.tag_match && item1.tag_match.ok === false;
                    return (
                      <MenuItem value={item1} key={index1}>
                        {item1.desc}
                        {tagBad && (
                          <span style={{ color: '#f83245', marginLeft: 6, fontSize: 12 }}>
                            ⚠ Tag requirement not met
                          </span>
                        )}
                      </MenuItem>
                    );
                  })}
                {siteSetting()?.settings?.backlog == 1 && <MenuItem value={0}>Backlog</MenuItem>}
                <MenuItem value={-1}>Dispatch to CFS Status</MenuItem>
              </Select>
            </FormControl>
          ))
        ) : (
          <FormControl variant="outlined" {...(formValid && { error: formValid.driver_id === -1 })}>
            <InputLabel>Select Drivers</InputLabel>
            <Select value={selDriver} onChange={e => handleSelectDriver(e.target.value)}>
              {siteSetting()?.settings?.backlog == 1 && <MenuItem value={0}>Backlog</MenuItem>}
              <MenuItem value={-1}>Dispatch to CFS Status</MenuItem>
            </Select>
          </FormControl>
        )}
      </div>

      <Dialog open={!!tagConfirm} onClose={() => setTagConfirm(false)}>
        <DialogTitle id="tag-confirm-title">Tag requirement not met</DialogTitle>
        <DialogContent>
          <DialogContentText id="tag-confirm-description">
            {`${tagConfirm?.desc || 'This driver'} does not meet the trip's tag requirements: ${tagIssuesText(tagConfirm?.tag_match)}. Dispatch anyway?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              const driver = tagConfirm;
              setTagConfirm(false);
              applyDriver(driver);
            }}
            color="primary"
            autoFocus
          >
            Dispatch anyway
          </Button>
          <Button onClick={() => setTagConfirm(false)} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </StepContent>
  );
};

export default Step3;

const StepContent = styled(JobSectionContent)``;
